/** Overview "My site" table — the customer's sites from /waf/domains, with
 * per-feature quick links (DNS / DEM / Multi-CDN / Origin Shield). */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, Globe } from 'lucide-react';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import { Card, CardBody, CardTitle, Button, Input, Skeleton } from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import { useWafDomains, type WafDomain } from '@/api/waf';
import styles from './WafDashboard.module.css';

/** DNS-status cell label + color class. */
function dnsLabel(status?: string): { text: string; cls: string } {
  const s = (status ?? '').toLowerCase();
  if (s === 'active' || s === 'completed' || s === '') return { text: 'DNS', cls: styles.feat };
  if (s === 'draft') return { text: 'DNS - draft', cls: styles.featWarn };
  return { text: `DNS - ${s}`, cls: styles.featWarn };
}

export function MySiteCard({ serviceId }: { serviceId: string }) {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const q = useWafDomains(serviceId);
  const all = q.data?.items ?? [];
  const items = useMemo(() => {
    const k = search.trim().toLowerCase();
    return k ? all.filter((d) => d.domain.toLowerCase().includes(k)) : all;
  }, [all, search]);

  const featLink = (slug: string, label: string, on: boolean) => (
    <a
      className={on ? styles.feat : styles.featOff}
      href="#"
      role="link"
      onClick={(e) => {
        e.preventDefault();
        if (on) navigate(`/services/waf/${serviceId}/${slug}`);
      }}
    >
      {label}
    </a>
  );

  const columns = useMemo<ColumnDef<WafDomain, unknown>[]>(
    () => [
      {
        id: 'name',
        header: 'Site name',
        enableSorting: false,
        cell: ({ row }) => (
          <a
            className={styles.feat}
            href="#"
            onClick={(e) => { e.preventDefault(); navigate(`/services/waf/${serviceId}/domains`); }}
          >
            {row.original.domain}
          </a>
        ),
      },
      {
        id: 'dns',
        header: 'DNS',
        enableSorting: false,
        cell: ({ row }) => {
          const l = dnsLabel(row.original.dns_status);
          return <span className={l.cls}>{l.text}</span>;
        },
      },
      {
        id: 'dem',
        header: 'Digital experience monitoring',
        enableSorting: false,
        cell: ({ row }) => featLink('experience', 'DEM', row.original.enable),
      },
      {
        id: 'mcdn',
        header: 'Multi-CDN',
        enableSorting: false,
        cell: ({ row }) => featLink('cdn', 'Multi-CDN', (row.original.platforms?.length ?? 0) > 0),
      },
      {
        id: 'shield',
        header: 'Origin Shield',
        enableSorting: false,
        cell: ({ row }) => featLink('origin-shield', 'Origin Shield', row.original.enable),
      },
    ],
    [serviceId, navigate],
  );

  return (
    <Card>
      <CardBody>
        <div className={styles.toolbar}>
          <CardTitle>My site</CardTitle>
          <div className={styles.search}>
            <Input
              placeholder="Enter some keyword..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              startSlot={<Search size={15} />}
              aria-label="Search sites"
            />
          </div>
          <Button
            variant="secondary"
            startIcon={<Plus size={15} />}
            style={{ marginLeft: 'auto' }}
            onClick={() => navigate(`/services/waf/${serviceId}/sites/new`)}
          >
            Add Site
          </Button>
        </div>
        {q.isPending ? (
          <Skeleton style={{ height: 200 }} />
        ) : q.isError ? (
          <Alert tone="danger" title="Couldn't load sites" actions={<button onClick={() => q.refetch()}>Retry</button>}>
            The site list couldn’t be loaded.
          </Alert>
        ) : (
          <DataTable
            columns={columns}
            data={items}
            getRowId={(r) => r.domain}
            emptyState={<EmptyState icon={<Globe size={20} />} title="No sites yet" description="No sites have been added to this WAF service." />}
          />
        )}
      </CardBody>
    </Card>
  );
}

export default MySiteCard;
